import React, { useState, useEffect } from 'react';
import { ExternalLink, Users, TrendingUp, Calendar, MessageSquare } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';

interface CommunityPost {
  id: string;
  title: string;
  content: string;
  category: 'opportunities' | 'news' | 'events' | 'discussions';
  author_name: string;
  link_url: string | null;
  image_url: string | null;
  is_featured: boolean;
  view_count: number;
  created_at: string;
}

const CATEGORY_CONFIG = {
  opportunities: { icon: TrendingUp, color: 'text-green-500', bg: 'bg-green-500/10' },
  news: { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  events: { icon: Calendar, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  discussions: { icon: Users, color: 'text-orange-500', bg: 'bg-orange-500/10' }
};

export default function WorkdayCommunityPosts() {
  const { theme } = useWorkdayClientTheme();
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      console.log('Fetching community posts...');
      const { data: session } = await supabase.auth.getSession();
      console.log('Current session:', session);

      const { data, error } = await supabase
        .from('workday_community_posts')
        .select('*')
        .eq('is_active', true)
        .order('is_featured', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) {
        console.error('Error fetching community posts:', error);
        throw error;
      }

      console.log('Fetched posts:', data);
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const incrementViewCount = async (postId: string) => {
    try {
      const post = posts.find(p => p.id === postId);
      if (!post) return;

      await supabase
        .from('workday_community_posts')
        .update({ view_count: post.view_count + 1 })
        .eq('id', postId);
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  if (loading) {
    return (
      <div className={`rounded-lg p-6 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
      }`}>
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className={`rounded-lg p-6 ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
    }`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Users className="w-6 h-6 text-primary" />
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Perplexity's Workday Community
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {posts.map((post) => {
          const CategoryIcon = CATEGORY_CONFIG[post.category].icon;

          return (
            <div
              key={post.id}
              className={`p-5 rounded-lg transition-all duration-200 hover:scale-105 ${
                theme === 'dark'
                  ? 'bg-gray-700/50 hover:bg-gray-700'
                  : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {post.is_featured && (
                <div className="mb-3">
                  <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-500/20 text-yellow-600">
                    Featured
                  </span>
                </div>
              )}

              <div className="flex items-center space-x-2 mb-3">
                <div className={`p-2 rounded ${CATEGORY_CONFIG[post.category].bg}`}>
                  <CategoryIcon className={`w-4 h-4 ${CATEGORY_CONFIG[post.category].color}`} />
                </div>
                <span className={`text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                </span>
              </div>

              <h3 className={`text-lg font-bold mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {post.title}
              </h3>

              <p className={`text-sm mb-4 line-clamp-3 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {post.content}
              </p>

              <div className="flex items-center justify-between">
                <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                  {post.view_count} views
                </div>
                {post.link_url && (
                  <a
                    href={post.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => incrementViewCount(post.id)}
                    className="inline-flex items-center text-primary hover:text-primary/80 text-sm font-semibold"
                  >
                    Learn More
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
